import { useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import EnsaioCompressaoPDF from '../components/EnsaioCompressaoPDF';
import { LOGO_EXEMPLO, ASSINATURA_EXEMPLO } from '../components/pdf/exemploImagens';
import { calcularFcj, calcularFcjEsperado, calcularFcmj, classificarFcj } from '../utils/fcjCalculo';

// Página de desenvolvimento: mostra o PDF do relatório sequencial ao vivo (hot reload),
// sem precisar baixar o arquivo. Acesse em /app/dev-pdf-preview. Não aparece no menu.
// Fcj/Fcj esp/cor são calculados aqui a partir de fcjCalculo.js (mesma lógica de
// normalizeResultadoSequencial), então mudanças na fórmula também aparecem no preview.

const FCK_MOCK = 30;
const CIMENTO_MOCK = 'CP II-Z-32';
const PONDERADOR_MOCK = 0.25;

const cabecalhoMock = {
  cliente: 'Construtora Exemplo Ltda',
  obra: 'Edifício Aurora',
  contato: 'Maria Silva',
  email: 'maria.silva@exemplo.com',
  cnpj: '12.345.678/0001-90',
  telefone: '(11) 91234-5678',
  local: 'São Paulo - SP',
  endereco: 'Rua Exemplo, 123 - Bairro - Cidade/UF',
  cimento: CIMENTO_MOCK,
  fck: String(FCK_MOCK),
  tipoConcreto: 'Convencional',
  tipoCorpoProva: 'Cilíndrico 10x20cm',
  concreteira: 'Concreteira Modelo',
  tipoCapeamento: 'Enxofre',
  identificacaoRelatorio: 'nº 001/2026',
};

// Dados "crus", equivalentes ao que a API devolveria antes da normalização.
const resultadosBrutosBase = [
  {
    id: '1',
    etiqueta: 'CP-001',
    notaFiscal: '123456',
    volume: 8,
    pecaConcretada: 'Pilar P12',
    dataSaidaUsina: '2026-07-10T07:30:00',
    dataInicioDescarga: '2026-07-10T08:10:00',
    dataFimDescarga: '2026-07-10T08:30:00',
    dataMoldagem: '2026-07-10T08:15:00',
    dataRuptura: '2026-08-07T09:00:00',
    idadeRuptura: 28,
    slumpAplicacao: 12,
    slumpNotaFiscal: 10,
    adicaoAgua: 5,
    temperaturaConcreto: 27,
    valrCarga: 42.5,
    ponderadorTipoCarga: 1,
    ponderadorTipoCorpoProva: 1,
    tipoRuptura: 'Cônica',
    prensa: 'Prensa 01',
  },
  {
    id: '2',
    etiqueta: 'CP-002',
    notaFiscal: '123457',
    volume: 8,
    pecaConcretada: 'Viga V04',
    dataSaidaUsina: '2026-07-10T07:30:00',
    dataInicioDescarga: '2026-07-10T08:10:00',
    dataFimDescarga: '2026-07-10T08:30:00',
    dataMoldagem: '2026-07-10T08:15:00',
    dataRuptura: '2026-08-07T09:05:00',
    idadeRuptura: 28,
    slumpAplicacao: 11,
    slumpNotaFiscal: 10,
    adicaoAgua: 0,
    temperaturaConcreto: 28,
    valrCarga: 33.1,
    ponderadorTipoCarga: 1,
    ponderadorTipoCorpoProva: 1,
    tipoRuptura: 'Cisalhamento',
    prensa: 'Prensa 01',
  },
  {
    id: '3',
    etiqueta: 'CP-003',
    notaFiscal: '123458',
    volume: 8,
    pecaConcretada: 'Laje L02',
    dataSaidaUsina: '2026-07-10T07:30:00',
    dataInicioDescarga: '2026-07-10T08:10:00',
    dataFimDescarga: '2026-07-10T08:30:00',
    dataMoldagem: '2026-07-10T08:15:00',
    dataRuptura: '2026-08-07T09:10:00',
    idadeRuptura: 28,
    slumpAplicacao: 13,
    slumpNotaFiscal: 10,
    adicaoAgua: 8,
    temperaturaConcreto: 29,
    valrCarga: 20.0,
    ponderadorTipoCarga: 1,
    ponderadorTipoCorpoProva: 1,
    tipoRuptura: 'Explosiva',
    prensa: 'Prensa 02',
  },
  {
    id: '4',
    etiqueta: 'CP-004',
    notaFiscal: '123459',
    volume: 8,
    pecaConcretada: 'Sapata S07',
    dataSaidaUsina: '2026-07-10T07:30:00',
    dataInicioDescarga: '2026-07-10T08:10:00',
    dataFimDescarga: '2026-07-10T08:30:00',
    dataMoldagem: '2026-07-10T08:15:00',
    dataRuptura: '2026-08-07T09:15:00',
    idadeRuptura: 28,
    slumpAplicacao: 12,
    slumpNotaFiscal: 10,
    adicaoAgua: 3,
    temperaturaConcreto: 26,
    valrCarga: 45.0, // fcj = 45 >= 1,20 x fcmj (36,6) => classifica como azul
    ponderadorTipoCarga: 1,
    ponderadorTipoCorpoProva: 1,
    tipoRuptura: 'Cônica',
    prensa: 'Prensa 02',
  },
];

// Linhas extras geradas a partir das 4 acima, só para forçar quebra de página no preview.
const resultadosBrutos = [
  ...resultadosBrutosBase,
  ...Array.from({ length: 16 }, (_, i) => {
    const numero = i + 5;
    return {
      ...resultadosBrutosBase[i % resultadosBrutosBase.length],
      id: String(numero),
      etiqueta: `CP-${String(numero).padStart(3, '0')}`,
      notaFiscal: String(123459 + numero),
    };
  }),
];

const resultadosMock = resultadosBrutos.map((item) => {
  const fcj = calcularFcj({
    valrCarga: item.valrCarga,
    ponderadorTipoCarga: item.ponderadorTipoCarga,
    ponderadorTipoCorpoProva: item.ponderadorTipoCorpoProva,
  });
  const fcjEsp = calcularFcjEsperado({ fck: FCK_MOCK, idade: item.idadeRuptura, ponderadorTipoCimento: PONDERADOR_MOCK });
  const fcmj = calcularFcmj({ fck: FCK_MOCK, idade: item.idadeRuptura, ponderadorTipoCimento: PONDERADOR_MOCK });

  return {
    ...item,
    fcj,
    fcjEsp,
    corFcj: classificarFcj({ fcj, fcmj, fcjEsperado: fcjEsp }),
  };
});

function DevPdfPreviewPage() {
  const [comImagens, setComImagens] = useState(true);

  return (
    <div style={{ height: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <input type="checkbox" checked={comImagens} onChange={(e) => setComImagens(e.target.checked)} />
        Com logo/assinaturas de exemplo
      </label>
      <div style={{ flex: 1 }}>
        <PDFViewer key={String(comImagens)} width="100%" height="100%" showToolbar>
          <EnsaioCompressaoPDF
            resultados={resultadosMock}
            cabecalho={cabecalhoMock}
            logoCliente={comImagens ? LOGO_EXEMPLO : null}
            assinaturas={comImagens ? [{ src: ASSINATURA_EXEMPLO, nome: 'Eng. Fulano de Tal' }, { src: ASSINATURA_EXEMPLO, nome: 'Eng. Beltrano da Silva' }] : []}
          />
        </PDFViewer>
      </div>
    </div>
  );
}

export default DevPdfPreviewPage;
